import React, { useMemo, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';

// Permission tick-list, grouped into the five display sections the server's
// permission registry defines (Dashboards / Workflow — Submit / Workflow —
// Review / Management / System) — not by the internal module prefix. Shared
// between the user modals (Accounts) and the role editor (/settings/roles).
// `allPermissions` rows come from GET /api/admin/permissions, which enriches
// each row with { key, label, group } from the registry; `groups` (optional)
// is the section order from the same payload.
//
// Layout: one full-width row per permission — a two-column grid would wrap
// every label and read as a wall of text. The internal
// key (`qm:view`) is NOT rendered: administrators recognise the wording, not
// the key (it stays on /settings/permission-reference and in the row tooltip).
// Long sections stay reachable via search, collapsible sections and an inner
// scroll area, so the Save button never gets pushed off the modal.
//
// `locked` (optional, { key: 'via Admin, Manager' }) marks keys the account
// already holds through its roles: rendered ticked and disabled with the
// source alongside, never toggled or counted as part of `selected`, and left
// alone by the bulk helpers. What remains tickable is the extras.

const DEFAULT_GROUPS = ['Dashboards', 'Workflow — Submit', 'Workflow — Review', 'Management', 'System'];

// Registry labels read '<page/flow name> — <action>'; split so the name can
// carry the visual weight and the action sits back in muted text.
const splitLabel = (text) => {
  const at = text.indexOf(' — ');
  if (at === -1) return { name: text, action: '' };
  return { name: text.slice(0, at), action: text.slice(at + 3) };
};

const PermissionCheckboxes = ({ allPermissions, groups, selected, onChange, disabled = false, hideModules = [], locked = {} }) => {
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const isLocked = (key) => Object.prototype.hasOwnProperty.call(locked, key);

  const sections = useMemo(() => {
    const order = [...(groups && groups.length ? groups : DEFAULT_GROUPS), 'Other'];
    const bySection = new Map(order.map((g) => [g, []]));
    for (const p of allPermissions) {
      if (hideModules.includes(p.module)) continue;
      if (p.adminOnly) continue; // Admin role's '*' only — never offered here
      const key = p.key || `${p.module}:${p.action}`;
      const group = bySection.has(p.group) ? p.group : 'Other';
      const text = p.label || p.description || key;
      bySection.get(group).push({ ...p, key, ...splitLabel(text) });
    }
    return order
      .map((g) => ({ group: g, perms: bySection.get(g) }))
      .filter((s) => s.perms.length > 0);
  }, [allPermissions, groups, hideModules]);

  const visibleKeys = useMemo(
    () => sections.flatMap((s) => s.perms.map((p) => p.key)),
    [sections],
  );
  const lockedCount = visibleKeys.filter(isLocked).length;
  const selectedCount = selected.filter((k) => visibleKeys.includes(k) && !isLocked(k)).length;

  // Search matches the wording an administrator sees, plus the key so a
  // developer pasting `workflow:esc:review` still lands on the right row.
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return sections;
    return sections
      .map(({ group, perms }) => ({
        group,
        perms: perms.filter((p) => `${p.name} ${p.action} ${p.key}`.toLowerCase().includes(q)),
      }))
      .filter((s) => s.perms.length > 0);
  }, [sections, q]);

  const toggle = (key) => {
    if (isLocked(key)) return;
    const set = new Set(selected);
    if (set.has(key)) set.delete(key); else set.add(key);
    onChange([...set]);
  };

  // Bulk helpers never touch keys hidden by `hideModules` — they are not on
  // screen, so clearing them would be an invisible change — nor locked ones.
  const setMany = (keys, on) => {
    const set = new Set(selected);
    keys.filter((k) => !isLocked(k)).forEach((k) => (on ? set.add(k) : set.delete(k)));
    onChange([...set]);
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white">
      <div className="flex items-center gap-3 px-3 py-2.5 border-b border-slate-200 bg-slate-50 rounded-t-lg">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search permissions…"
            className="w-full pl-8 pr-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <span className="text-xs text-slate-500 whitespace-nowrap">
          {lockedCount > 0 && (
            <><span className="font-semibold text-slate-700">{lockedCount}</span> from roles · </>
          )}
          <span className="font-semibold text-slate-700">{selectedCount}</span>{lockedCount > 0 ? ' extra' : ` of ${visibleKeys.length} selected`}
        </span>
        {!disabled && selectedCount > 0 && (
          <button
            type="button"
            onClick={() => setMany(visibleKeys, false)}
            className="text-xs text-slate-500 hover:text-slate-700 whitespace-nowrap"
          >
            Clear All
          </button>
        )}
      </div>

      {/* pt-0: the section headers are sticky — any top padding would let the
          previous row peek out above them while scrolling. */}
      <div className="max-h-[22rem] overflow-y-auto px-2 pt-0 pb-2">
        {filtered.length === 0 && (
          <p className="px-2 py-6 text-sm text-slate-500 text-center">No permission matches “{query}”.</p>
        )}
        {filtered.map(({ group, perms }) => {
          const keys = perms.map((p) => p.key);
          const on = keys.filter((k) => selected.includes(k) || isLocked(k)).length;
          const free = keys.filter((k) => !isLocked(k));
          const freeOn = free.filter((k) => selected.includes(k)).length;
          const isCollapsed = !q && collapsed[group];
          return (
            <div key={group} className="mb-1 last:mb-0">
              <div className="sticky top-0 z-10 flex items-center gap-2 px-1 py-2 bg-white border-b border-slate-100">
                <button
                  type="button"
                  onClick={() => setCollapsed((c) => ({ ...c, [group]: !c[group] }))}
                  className="flex items-center gap-1.5 min-w-0 text-left"
                >
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider truncate">{group}</span>
                  <span className="text-xs text-slate-400 whitespace-nowrap">{on}/{keys.length}</span>
                </button>
                {!disabled && free.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setMany(free, freeOn !== free.length)}
                    className="ml-auto text-xs text-blue-600 hover:text-blue-700 whitespace-nowrap"
                  >
                    {freeOn === free.length ? 'Clear Section' : 'Select All'}
                  </button>
                )}
              </div>

              {!isCollapsed && (
                <div className="space-y-0.5 pb-1">
                  {perms.map((p) => {
                    const lockedBy = isLocked(p.key) ? locked[p.key] : null;
                    const checked = !!lockedBy || selected.includes(p.key);
                    return (
                      <label
                        key={p.key}
                        title={lockedBy ? `${p.key} — ${lockedBy}` : p.key}
                        className={`flex items-start gap-2.5 px-2.5 py-2 rounded-lg border transition-colors ${
                          lockedBy ? 'border-slate-200 bg-slate-50'
                            : checked ? 'border-blue-200 bg-blue-50/70' : 'border-transparent hover:bg-slate-50'
                        } ${disabled || lockedBy ? 'opacity-80' : 'cursor-pointer'}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(p.key)}
                          disabled={disabled || !!lockedBy}
                          className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 shrink-0"
                        />
                        <span className="min-w-0 text-sm leading-5 flex-1">
                          <span className={checked && !lockedBy ? 'font-medium text-blue-900' : 'font-medium text-slate-800'}>{p.name}</span>
                          {p.action && <span className="text-slate-500"> — {p.action}</span>}
                        </span>
                        {lockedBy && (
                          <span className="shrink-0 text-xs text-slate-500 whitespace-nowrap mt-0.5">{lockedBy}</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PermissionCheckboxes;
